import React, { useState } from 'react';
import { FaSearch, FaSyncAlt, FaTimes, FaPlus, FaFileImport, FaTrashRestore } from 'react-icons/fa';
import { DataFailure, DataTable, approxRows, filterTables, kindLabel, tableKey, tableLabel } from './dataView';

interface TableListProps {
    tables: DataTable[];
    status: 'loading' | 'ready' | 'failed';
    /** A refusal of the list call alone (an unavailable database replaces the whole view instead). */
    failure: DataFailure | null;
    selectedKey: string | null;
    onSelect: (table: DataTable) => void;
    onRefresh: () => void;
    /** Editing on: New table and Import CSV (a new table from a file), and a Deleted (N) group for the trash with Restore. */
    onNewTable?: () => void;
    onImportCsv?: () => void;
    onRestore?: (trashName: string) => void;
}

/**
 * The database's tables, views and materialized views, with a filter. Names are the customer's own: rendered as
 * text. The filter matches `schema.name` in any letter case; the schema is shown only when it is not `public`.
 */
/** A trashed table's name without its __timestamp suffix (DESIGN_EDITING.md E11). */
const trashDisplayName = (name: string): string => name.replace(/__\d{14}$/, '');

const TableList: React.FC<TableListProps> = ({ tables, status, failure, selectedKey, onSelect, onRefresh, onNewTable, onImportCsv, onRestore }) => {
    const [filter, setFilter] = useState('');
    const [trashOpen, setTrashOpen] = useState(false);
    const live = tables.filter((t) => t.schema !== 'redu_trash');
    const trash = tables.filter((t) => t.schema === 'redu_trash');
    const shown = filterTables(live, filter);

    return (
        <div className="db-data-pane db-data-tables">
            <div className="db-data-pane-header">
                <span className="db-data-pane-title">
                    Tables{status === 'ready' ? ` (${live.length})` : ''}
                </span>
                <div className="db-data-pane-actions">
                    <button
                        type="button"
                        className="db-data-icon-button"
                        onClick={onRefresh}
                        disabled={status === 'loading'}
                        aria-label="Refresh the table list"
                        title="Refresh the table list"
                    >
                        <FaSyncAlt />
                    </button>
                </div>
            </div>

            {(onNewTable || onImportCsv) && (
                <div className="db-data-create-row">
                    {onNewTable && (
                        <button type="button" className="db-data-text-button db-data-create-button" onClick={onNewTable}>
                            <FaPlus aria-hidden="true" /> New table
                        </button>
                    )}
                    {onImportCsv && (
                        <button type="button" className="db-data-text-button" onClick={onImportCsv} title="A new table from a CSV file">
                            <FaFileImport aria-hidden="true" /> Import CSV
                        </button>
                    )}
                </div>
            )}

            <div className="instance-search-bar db-data-filter">
                <FaSearch className="instance-search-icon" />
                <input
                    type="text"
                    className="instance-search-input"
                    placeholder="Filter tables..."
                    aria-label="Filter tables"
                    value={filter}
                    onChange={(e) => setFilter(e.target.value)}
                />
                {filter && (
                    <button type="button" className="instance-search-clear" onClick={() => setFilter('')} aria-label="Clear filter">
                        <FaTimes />
                    </button>
                )}
            </div>

            {status === 'loading' ? (
                <div className="loading-state"><FaSyncAlt className="spin" /> Loading tables...</div>
            ) : status === 'failed' && failure ? (
                <div className="db-data-error" role="alert">
                    <p className="db-data-error-message" title={failure.apiMessage}>{failure.message}</p>
                    <button type="button" className="btn btn-secondary" onClick={onRefresh}>Retry</button>
                </div>
            ) : live.length === 0 && trash.length === 0 ? (
                <div className="empty-state db-data-empty-state">No tables yet</div>
            ) : shown.length === 0 && !(filter === '' && trash.length > 0) ? (
                <div className="empty-state db-data-empty-state">No match</div>
            ) : (
                <ul className="db-data-table-list">
                    {shown.map((t) => {
                        const key = tableKey(t);
                        const kind = kindLabel(t.kind);
                        const rows = approxRows(t.estimated_rows);
                        return (
                            <li key={key}>
                                <button
                                    type="button"
                                    className="db-data-table-button"
                                    aria-pressed={selectedKey === key}
                                    onClick={() => onSelect(t)}
                                    title={`${t.schema}.${t.name}`}
                                >
                                    <span className="db-data-table-name">{tableLabel(t)}</span>
                                    {(kind || rows) && (
                                        <span className="db-data-table-meta">
                                            {[kind, rows].filter(Boolean).join(' · ')}
                                        </span>
                                    )}
                                </button>
                            </li>
                        );
                    })}
                </ul>
            )}

            {onRestore && trash.length > 0 && (
                <div className="db-data-trash">
                    <button type="button" className="db-data-trash-toggle" aria-expanded={trashOpen} onClick={() => setTrashOpen((o) => !o)}>
                        {trashOpen ? '▾' : '▸'} Deleted ({trash.length})
                    </button>
                    {trashOpen && (
                        <ul className="db-data-trash-list">
                            {trash.map((t) => (
                                <li key={tableKey(t)}>
                                    <span className="db-data-trash-name" title={t.name}>{trashDisplayName(t.name)}</span>
                                    <button type="button" className="db-data-icon-button" aria-label={`Restore ${trashDisplayName(t.name)}`} title="Restore" onClick={() => onRestore(t.name)}>
                                        <FaTrashRestore />
                                    </button>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            )}
        </div>
    );
};

export default TableList;
